import { translateManager as t } from 'services/TranslateManager'
import {
  getOrderSummarySizeText,
  getSelectedSize,
  getSizeName,
} from 'services/SizesManager'
import { promoCodeManager } from 'services/PromoCodeManager'
import { DISCOUNT_TYPES } from '../../../stores/promoCodeStore/promoCode.consts'
import { formatPrice } from '../../../utils/utils'
import SummaryItem from 'components/SummaryItem/SummaryItem'
import { MIXED_SIZES } from 'pages/PhotoStyler/TileDesignerConsts'
import { logger } from 'services/logger'
import { ProductIds, BookSize } from '@mixtiles/web-backend-shared'
import { PRODUCT_TYPES } from '../../../services/ProductTypeState'
import { getMaterialToName } from '../../../services/tileMaterial'
import { isBookCreditsCoupon } from '../../../pages/GiftCard/utils'

export const renderFreeTilesText = ({ numOfTiles, sizes, productType }) => {
  // Check if this is for books
  const isBooks =
    sizes &&
    sizes.some((size) => size in BookSize) &&
    productType === PRODUCT_TYPES.PHOTO_BOOK

  if (isBooks) {
    return
  }

  const freeTilesText =
    numOfTiles > 1
      ? t.get('order.checkout.payment_summary_rows.free_tiles_many', {
          v1: numOfTiles,
        })
      : t.get('order.checkout.payment_summary_rows.free_tiles_single')

  return freeTilesText
}

export const getFreeTilesTitle = (amount) => {
  return amount === 1
    ? t.get('order.checkout.payment_summary_rows.free_tiles_single')
    : t.get('order.checkout.payment_summary_rows.free_tiles_many', {
        v1: amount,
      })
}

export const getBundleActionText = (data) => {
  if (data.bundlePriceData) {
    if (data.bundlePriceData.tileSizesList.length > 0) {
      const selectedSize = getSelectedSize()
      if (!data.bundlePriceData.tileSizesList.includes(selectedSize)) {
        return {
          text: t.get(
            'order.checkout.payment_summary_rows.bundle_change_to_size',
            { v1: getSizeName(data.bundlePriceData.tileSizesList[0]) }
          ),
        }
      }
    }

    const { bundlePriceTilesCount, howManyToBundlePrice, bundleTiles } =
      data.bundlePriceData
    const noBundleUsed = !howManyToBundlePrice && bundlePriceTilesCount === 0
    if (noBundleUsed || howManyToBundlePrice > 0) {
      const tilesToAdd = noBundleUsed ? bundleTiles : howManyToBundlePrice
      let tilesToAddText =
        tilesToAdd > 1
          ? t.get(
              'order.checkout.payment_summary_rows.bundle_action.add_many',
              { v1: tilesToAdd }
            )
          : t.get('order.checkout.payment_summary_rows.bundle_action.add_one')
      if (bundlePriceTilesCount) {
        tilesToAddText =
          tilesToAdd > 1
            ? t.get(
                'order.checkout.payment_summary_rows.bundle_action.add_another_many',
                { v1: tilesToAdd }
              )
            : t.get(
                'order.checkout.payment_summary_rows.bundle_action.add_another_one'
              )
      }
      return { text: tilesToAddText, applied: !!bundlePriceTilesCount }
    }

    if (!noBundleUsed) {
      const tilePrice = data.bundlePriceData.bundleProductPrice
      const bundleDiscount =
        bundlePriceTilesCount * tilePrice.value -
        data.bundlePriceData.totalBundlePrice
      if (bundleDiscount <= 0) {
        logger.error('Invalid bundle discount calculated', null, {
          bundleDiscount,
        })
        return {}
      }

      return {
        text: t.get(
          'order.checkout.payment_summary_rows.bundle_action.perfect',
          { v1: formatPrice(bundleDiscount, data.currency) }
        ),
        applied: true,
        fullyApplied: true,
      }
    }
  }
}

export const getBuyXGetYActionText = (data) => {
  if (getSelectedSize() === MIXED_SIZES) {
    return { text: 'Not supported for mixed sizes' }
  }

  const { buy, get } =
    data.buyXGetYDiscountData ||
    promoCodeManager.getMetadata(DISCOUNT_TYPES.BUYXGETY)
  if (!data.buyXGetYDiscountData && !data.buyXgetY) {
    return { text: '' }
  }
  const { usedCredit, unusedCredit, howManyForNextFree } =
    data.buyXGetYDiscountData || data.buyXgetY
  if (unusedCredit === get) {
    // The free tiles credit is not used.
    return {
      text: t.get(
        `order.checkout.payment_summary_rows.buy_x_get_y_action.${
          get > 1 ? 'yay_get_many' : 'yay_get_one'
        }`,
        { v1: get }
      ),
    }
  }
  if (unusedCredit > 0) {
    // The free tiles credit is partially not used.
    return {
      text: t.get(
        `order.checkout.payment_summary_rows.buy_x_get_y_action.${
          unusedCredit > 1 ? 'get_more_many' : 'get_more_one'
        }`,
        { v1: unusedCredit }
      ),
    }
  }

  if (data.additionalTilesSize > 0 && !howManyForNextFree) {
    // All of the free tiles credit is used.
    const { buyXGetYDiscountData, additionalTilePrice } = data
    const price = buyXGetYDiscountData
      ? buyXGetYDiscountData.amountSaved
      : additionalTilePrice * usedCredit
    return {
      text: t.get(
        'order.checkout.payment_summary_rows.buy_x_get_y_action.saved',
        { v1: formatPrice(price, data.currency) }
      ),
      fullyApplied: true,
    }
  }

  // User should add more tiles to use this promocode credit.
  const tilesToAdd = data.additionalTilesSize === 0 ? buy : howManyForNextFree
  return {
    text:
      tilesToAdd === 1
        ? t.get(
            `order.checkout.payment_summary_rows.add_one_to.${
              get > 1 ? 'get_more_many' : 'get_more_one'
            }`,
            { v1: get }
          )
        : t.get(
            `order.checkout.payment_summary_rows.add_more_to.${
              get > 1 ? 'get_more_many' : 'get_more_one'
            }`,
            {
              v1: tilesToAdd,
              v2: get,
            }
          ),
  }
}

// If isFixedOrderSize is true, the user cannot change the amount of tiles in the order.
// In this case, we won't show the texts that require the user to add more tiles to get the discount.
export function getDiscountDescription({
  discountData,
  isFixedOrderSize,
  priceBeforeDiscount,
  priceAfterDiscount,
}) {
  const discountType =
    discountData.discountType || promoCodeManager.getDiscountCouponType()
  let title
  let subtitle
  let isDiscountApplied = priceBeforeDiscount > priceAfterDiscount
  let discountFullyApplied
  switch (discountType) {
    case DISCOUNT_TYPES.PERCENT:
      title = t.get('order.checkout.payment_summary_rows.discount_percentage', {
        v1: discountData.discountPercent,
      })
      discountFullyApplied = true
      break
    case DISCOUNT_TYPES.BUNDLE: {
      const { bundleTiles, bundlePrice } = discountData.bundleDiscountData
      title = t.get('order.checkout.payment_summary_rows.tiles_size_many', {
        v1: bundleTiles,
        v2: formatPrice(bundlePrice, discountData.currency),
      })
      const { text, applied, fullyApplied } = getBundleActionText(discountData)
      subtitle = text
      isDiscountApplied = applied
      discountFullyApplied = fullyApplied
      break
    }
    case DISCOUNT_TYPES.BUYXGETY: {
      const { buy, get } =
        discountData.buyXGetYDiscountData ||
        promoCodeManager.getMetadata(DISCOUNT_TYPES.BUYXGETY)
      title = t.get('order.checkout.payment_summary_rows.buy_x_get_y', {
        v1: buy,
        v2: get,
      })
      const { text, fullyApplied } = getBuyXGetYActionText(discountData)
      subtitle = text
      discountFullyApplied = fullyApplied
      break
    }
    case DISCOUNT_TYPES.BUYOVER: {
      const metadata =
        discountData.buyOverDiscountData ||
        promoCodeManager.getMetadata(DISCOUNT_TYPES.BUYOVER)
      if (!metadata.buyOverPrice && metadata.buyOverPrice !== 0) {
        return null
      }
      const { buyOverPrice, buyOverDiscount, bundleCurrency = 'USD' } = metadata
      if (buyOverPrice === 0) {
        title = t.get(
          'order.checkout.payment_summary_rows.discount_percentage',
          {
            v1: buyOverDiscount,
          }
        )
      } else {
        title = t.get(
          'order.checkout.payment_summary_rows.discount_off_over_price',
          {
            v1: buyOverDiscount,
            v2: formatPrice(buyOverPrice, bundleCurrency),
          }
        )
      }

      const discountDataPrice = discountData.priceAfterPercentageDiscount
      if (discountDataPrice >= buyOverPrice) {
        const showsAppliedCopy =
          buyOverPrice !== 0 &&
          discountData.productType !== PRODUCT_TYPES.PHOTO_BOOK
        if (!showsAppliedCopy) {
          subtitle = ''
        } else {
          const totalSaved = priceBeforeDiscount - priceAfterDiscount
          subtitle = t.get(
            'order.checkout.payment_summary_rows.buy_over_price_applied_saved',
            { v1: formatPrice(totalSaved, bundleCurrency) }
          )
        }
        discountFullyApplied = true
      } else {
        subtitle = t.get(
          'order.checkout.payment_summary_rows.buy_over_price_buy_more',
          {
            v1: formatPrice(buyOverPrice - discountDataPrice, bundleCurrency),
          }
        )
      }
      break
    }
    default:
      break
  }

  if (!isFixedOrderSize) {
    return { title, subtitle }
  }

  if (discountFullyApplied) {
    return { title, subtitle }
  }

  // If we got here, it means that the user cannot change the amount of tiles in the order, so the message should be different.
  if (!isDiscountApplied) {
    return {}
  }

  return {
    title,
    subtitle: t.get(
      'order.checkout.payment_summary_rows.buy_x_get_y_action.saved',
      {
        v1: formatPrice(
          priceBeforeDiscount - priceAfterDiscount,
          discountData.currency
        ),
      }
    ),
  }
}

// Non flat pricing, non sizes
export const renderBaseTilesPriceForVariesPricingType = (data) => {
  if (
    data.minTilesForDiscountPrice &&
    data.minTilesForDiscountPrice > 0 &&
    data.bundlePriceData.totalBundlePrice === 0
  ) {
    let tilesDescription
    if (data.minTilesForDiscountPrice === 1) {
      tilesDescription = t.get('order.checkout.payment_summary_rows.one_tile')
    } else {
      tilesDescription = t.get(
        'order.checkout.payment_summary_rows.base_price_many',
        {
          v1: data.minTilesForDiscountPrice,
          v2: formatPrice(data.regularTilePrice, data.currency),
        }
      )
    }

    return (
      <SummaryItem text={tilesDescription}>
        {formatPrice(data.tilesForBasePriceTotalPrice, data.currency)}
      </SummaryItem>
    )
  }
}

// Min order size
export const renderMinOrderSizeLineItem = (data) => {
  if (data.minOrderSize && data.bundlePriceData.totalBundlePrice === 0) {
    return (
      <SummaryItem
        text={t.get('order.checkout.payment_summary_rows.tiles_size_many', {
          v1: data.minOrderSize,
          v2: formatPrice(data.minOrderPrice, data.currency),
        })}
      >
        {formatPrice(data.minOrderPrice, data.currency)}
      </SummaryItem>
    )
  }
}

export const renderAdditionalTilesPriceForVariesPricingType = (data) => {
  if (
    data.minTilesForDiscountPrice &&
    data.additionalTilesSize &&
    data.additionalTilesSize > 0 &&
    data.bundlePriceData.totalBundlePrice === 0
  ) {
    let tilesDescription
    if (data.additionalTilesSize === 1) {
      tilesDescription = t.get(
        'order.checkout.payment_summary_rows.more_tiles_one'
      )
    } else {
      tilesDescription = t.get(
        'order.checkout.payment_summary_rows.more_tiles_with_each_price',
        {
          v1: data.additionalTilesSize,
          v2: formatPrice(data.additionalTilePrice, data.currency),
        }
      )
    }

    return (
      <SummaryItem text={tilesDescription}>
        {formatPrice(data.additionalTilesTotalPrice, data.currency)}
      </SummaryItem>
    )
  }
}

export const renderAdditionalTilesPriceForMinOrderSize = (data) => {
  if (
    !data.minTilesForDiscountPrice &&
    data.additionalTilesSize &&
    data.additionalTilesSize > 0 &&
    data.minOrderSize &&
    data.bundlePriceData.totalBundlePrice === 0
  ) {
    let tilesDescription
    if (data.additionalTilesSize === 1) {
      tilesDescription = t.get(
        'order.checkout.payment_summary_rows.more_tiles_one'
      )
    } else {
      tilesDescription = t.get(
        'order.checkout.payment_summary_rows.more_tiles_many',
        { v1: data.additionalTilesSize }
      )
    }

    const text = <span>{tilesDescription}</span>
    return (
      <SummaryItem text={text}>
        {formatPrice(data.additionalTilesTotalPrice, data.currency)}
      </SummaryItem>
    )
  }
}

export const renderPriceAfterDiscount = (currency, priceBefore, priceAfter) => {
  return (
    <div>
      <span className="crossed-out">{formatPrice(priceBefore, currency)}</span>
      <span className="highlighted bold">
        {formatPrice(priceAfter, currency)}
      </span>
    </div>
  )
}

// Applied discounts. If withFreeTiles === false, don't include the tiles discount in the description
export function getAppliedDiscounts(data, withFreeTiles = true) {
  const discountDesc = []
  let appliedDiscount = false
  const discountType =
    data.discount?.type || promoCodeManager.getDiscountCouponType()

  if (
    withFreeTiles &&
    data.freeTilesData &&
    data.freeTilesData.freeTilesCreditUsed
  ) {
    if (data.freeTiles) {
      data.freeTiles > 1
        ? discountDesc.push(
            t.get('order.checkout.payment_summary_rows.are_free', {
              v1: data.freeTiles,
            })
          )
        : discountDesc.push(
            t.get('order.checkout.payment_summary_rows.is_free')
          )
    }
  }

  if (discountType) {
    if (discountType === DISCOUNT_TYPES.PERCENT) {
      discountDesc.push(
        t.get('order.checkout.payment_summary_rows.discount_percentage', {
          v1: data.discountPercent,
        })
      )
    }

    if (
      discountType === DISCOUNT_TYPES.BUYOVER &&
      data.buyOver &&
      data.buyOver.buyOverPrice &&
      data.priceBeforeAdditions >= data.buyOver.buyOverPrice
    ) {
      discountDesc.push(
        t.get('order.checkout.payment_summary_rows.discount_percentage', {
          v1: data.buyOver.buyOverDiscount,
        })
      )
    }

    if (data.bundlePriceData && data.bundlePriceData.totalBundlePrice > 0) {
      appliedDiscount = true
    }
  }

  appliedDiscount = appliedDiscount || discountDesc.length > 0
  return { appliedDiscount, discountDesc }
}

export function getTilesCreditsCouponDescription(tilesCreditsCoupon, t) {
  const materialToName = getMaterialToName(t)
  const tilesAmount = tilesCreditsCoupon.amount
  const tilesSize =
    tilesCreditsCoupon?.metadata?.tileSizesList?.length !== 1
      ? ''
      : `, ${getSizeName(tilesCreditsCoupon.metadata.tileSizesList[0])}`

  const materialType =
    tilesCreditsCoupon?.metadata?.materialTypes?.length !== 1 ||
    tilesCreditsCoupon?.metadata?.materialTypes[0] === 'classic'
      ? ''
      : `, ${materialToName[
          tilesCreditsCoupon?.metadata?.materialTypes[0]
        ]?.toLowerCase()}`

  const code = tilesCreditsCoupon.sourceCoupon
  return `${tilesAmount} ${
    isBookCreditsCoupon(tilesCreditsCoupon)
      ? t('order.checkout.promo.free_books')
      : t('order.checkout.promo.free_tiles')
  }${tilesSize}${materialType}: ${code}`
}

export function getGiftCardCreditsDescription(giftCardBalance, currency) {
  const { balance } = giftCardBalance

  return `${t.get('general.menu.gift_card')}: ${t.get(
    'order.checkout.gift_card_available_credits',
    {
      v1: formatPrice(balance, currency),
    }
  )}`
}

/**
 * Gets the display description for a gift card in the order summary.
 * Handles three types: value cards ($50), tile cards (5 tiles), and book cards (3 books).
 */
export function getGiftCardDescription(pricingSummary) {
  const { summaryItems, currency, productType, originalProducts, products } =
    pricingSummary

  const giftCardLabel = t.get('general.menu.gift_card')

  // Value gift cards (e.g., "$50 Gift Card")
  const valueGiftCards = summaryItems?.filter(
    (p) => p.productId === ProductIds.GIFT_CARD_VALUE
  )

  if (valueGiftCards?.length > 0) {
    if (productType === PRODUCT_TYPES.BUSINESS) {
      return t.get('business.employees_gift_cards')
    }

    const quantityPrefix =
      valueGiftCards.length > 1 ? `${valueGiftCards.length} ` : ''
    const price = formatPrice(valueGiftCards[0]?.priceAfterDiscount, currency)
    return `${quantityPrefix}${giftCardLabel} (${price})`
  }

  // Item gift cards: tiles or books
  const itemGiftCards = summaryItems?.filter(
    (p) =>
      p.productId === ProductIds.GIFT_CARD_TILES ||
      p.productId === ProductIds.GIFT_CARD_BOOKS
  )

  const quantityPrefix =
    itemGiftCards.length > 1 ? `${itemGiftCards.length} ` : ''
  const productId = products[0].productId

  let itemDetails
  if (productId === ProductIds.GIFT_CARD_BOOKS) {
    itemDetails = t.get('order.checkout.payment_summary_rows.many_books', {
      v1: originalProducts[0].value,
      v2: getOrderSummarySizeText(
        originalProducts[0].pricingProduct.tileSize,
        true
      ),
    })
  } else if (productId === ProductIds.GIFT_CARD_TILES) {
    itemDetails = t.get('order.checkout.payment_summary_rows.many_tiles', {
      v1: originalProducts[0].value,
    })
  }

  return `${quantityPrefix}${giftCardLabel} (${itemDetails})`
}
